import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import {
  ReviewSource,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDelayedWorkplaceReviewFlag } from "@src/appV2/redesign/Workplace/Review/useDelayedWorkplaceReviewFlag";

import { type CommentReactionSource } from "../types";
import { WorkplaceReviewCommentCompactCard } from "./CompactCard";

interface WorkplaceReviewCommentCompactCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onClick?: () => void;
  showEngagementActions?: boolean;
  isReadOnly?: boolean;
  isLocked?: boolean;
  onUnlockPress?: () => void;
  reactionSource: CommentReactionSource;
}

export function WorkplaceReviewCommentCompactCardContainer(
  props: WorkplaceReviewCommentCompactCardProps
) {
  const {
    comment,
    workplaceId,
    onLikeChanged,
    onClick,
    showEngagementActions,
    isReadOnly,
    isLocked,
    onUnlockPress,
    reactionSource,
  } = props;
  const {
    createdAt,
    workerInfo,
    text,
    type: commentType,
    workerReaction,
    aggregates,
    shiftPeriod,
    shiftWorkerQualification,
    patientsPerNurse,
    reviewTag,
    rating,
    media,
    source,
    externalDetails,
  } = comment.attributes;
  const { id: workerId, name: workerName } = workerInfo;

  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const { reviewsViewContext } = useWorkplaceModalsDataContext();

  useLogEffect(
    APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_LOCKED_COMMENT_VIEWED,
    { workplaceId, commentId: comment.id },
    { enabled: Boolean(isLocked) }
  );

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  const displayDateIso =
    isExternalReview && isDefined(externalDetails?.publishedAt)
      ? externalDetails.publishedAt
      : createdAt;

  return (
    <WorkplaceReviewCommentCompactCard
      commentId={comment.id}
      commentText={text}
      commentType={commentType === "WRITTEN" || commentType === "VIDEO" ? commentType : undefined}
      mediaUrl={media?.[0]?.url}
      createdAtIso={displayDateIso}
      totalLikes={aggregates.totalLikes}
      workerName={workerName}
      totalDislikes={aggregates.totalDislikes}
      workerReaction={workerReaction ?? undefined}
      profilePictureUrl={profilePictureUrl}
      isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
      workplaceId={workplaceId}
      maxLines={8}
      shiftPeriod={shiftPeriod}
      patientsPerNurse={patientsPerNurse}
      reviewTag={reviewTag}
      shiftWorkerQualification={shiftWorkerQualification}
      rating={rating}
      useDelayedReviewLayout={useDelayedReviewUI}
      showEngagementActions={showEngagementActions}
      isReadOnly={isReadOnly}
      isLocked={isLocked}
      reactionSource={reactionSource}
      viewContext={reviewsViewContext}
      source={source}
      reviewUrl={externalDetails?.reviewUrl}
      onUnlockPress={onUnlockPress}
      onClick={onClick}
      onLikeChanged={onLikeChanged}
    />
  );
}
